// Admin-only panel: generate, send, download & manage pre-approval letters
// for a given application. The letter PDF is rendered server-side using the
// official PMB Loans template (Ariel Lopez, Managing Partner).
import React, { useEffect, useState } from "react";
import { api, errorMessage, getToken } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Award, Send, Download, FileText, Plus, Trash2 } from "lucide-react";
import { fmtCurrency, fmtDateTime } from "../lib/constants";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PreApprovalPanel({ app }) {
  const appId = app.application_id;
  const [letters, setLetters] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    borrower_or_entity_name: "",
    loan_amount: "",
    letter_date: "",
  });

  const [sendOpen, setSendOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [recipients, setRecipients] = useState({
    broker: true,
    borrower: true,
    extra: "",
  });
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/applications/${appId}/pre-approval`);
      setLetters(data);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  useEffect(() => { load(); }, [appId]);

  const openGen = () => {
    setForm({
      borrower_or_entity_name:
        app.property_in_llc && app.llc_name ? app.llc_name : app.borrower_name || "",
      loan_amount: String(app.expected_loan_amount || ""),
      letter_date: "",
    });
    setGenOpen(true);
  };

  const generate = async (e) => {
    e.preventDefault();
    const amount = Number(form.loan_amount || 0);
    if (!form.borrower_or_entity_name.trim()) { toast.error("Borrower / entity name is required"); return; }
    if (!amount || amount <= 0) { toast.error("Loan amount must be greater than zero"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/applications/${appId}/pre-approval`, {
        borrower_or_entity_name: form.borrower_or_entity_name.trim(),
        loan_amount: amount,
        letter_date: form.letter_date || null,
      });
      toast.success("Pre-approval letter generated");
      setGenOpen(false);
      load();
    } catch (e2) {
      toast.error(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  const downloadLetter = (letter) => {
    const token = getToken();
    // Use a hidden link with fetch + blob so we keep the auth header
    fetch(`${BACKEND}/api/admin/pre-approval/${letter.letter_id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = letter.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error(err.message || "Download failed"));
  };

  const openSend = (letter) => {
    setSendTarget(letter);
    setRecipients({
      broker: !!app.sponsor_id,
      borrower: !!app.borrower_email,
      extra: "",
    });
    setSendOpen(true);
  };

  const submitSend = async () => {
    if (!sendTarget) return;
    const list = [];
    if (recipients.broker && app.sponsor_id) {
      // pull broker email from app sponsor object — for now use a placeholder; admin can edit via extra
    }
    if (recipients.borrower && app.borrower_email) list.push(app.borrower_email);
    if (recipients.extra) {
      for (const r of recipients.extra.split(",")) {
        const v = r.trim();
        if (v.includes("@")) list.push(v);
      }
    }
    // For broker: we know sponsor_id but not their email here. Resolve via a quick fetch.
    if (recipients.broker && app.sponsor_id) {
      try {
        const { data: brokers } = await api.get(`/admin/brokers`);
        const sp = brokers.find((b) => b.user_id === app.sponsor_id);
        if (sp?.email) list.push(sp.email);
      } catch (e) { /* fall through */ }
    }
    const unique = Array.from(new Set(list.map((x) => x.trim().toLowerCase()).filter(Boolean)));
    if (!unique.length) { toast.error("Pick at least one recipient"); return; }
    setSending(true);
    try {
      const { data } = await api.post(
        `/admin/pre-approval/${sendTarget.letter_id}/send`,
        { recipients: unique },
      );
      const okCount = data.results.filter((r) => r.ok).length;
      const failCount = data.results.length - okCount;
      if (okCount && !failCount) toast.success(`Letter sent to ${okCount} recipient${okCount > 1 ? "s" : ""}`);
      else if (okCount && failCount) toast.warning(`Sent to ${okCount}; ${failCount} failed (sandbox/verification)`);
      else toast.error("Send failed — check Resend domain verification");
      setSendOpen(false);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSending(false);
    }
  };

  const remove = async (letter) => {
    if (!window.confirm(`Delete this letter for ${letter.borrower_or_entity_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/pre-approval/${letter.letter_id}`);
      toast.success("Letter deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <Card className="border-slate-800 bg-slate-900 p-6" data-testid="pre-approval-panel">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Award className="h-4 w-4 text-emerald-300" />
          <div className="text-sm font-semibold">Pre-Approval Letters</div>
        </div>
        <Button
          onClick={openGen}
          size="sm"
          className="bg-emerald-600 text-white hover:bg-emerald-500 btn-glow"
          data-testid="pa-generate-btn"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Generate letter
        </Button>
      </div>

      {letters.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-800 p-6 text-center text-xs text-slate-500">
          No pre-approval letters yet. Generate one to email it directly to the broker or borrower.
        </div>
      ) : (
        <ul className="space-y-2">
          {letters.map((l) => (
            <li
              key={l.letter_id}
              className="flex flex-wrap items-center gap-3 rounded-md border border-slate-800 bg-slate-950 p-3"
              data-testid={`pa-letter-${l.letter_id}`}
            >
              <FileText className="h-4 w-4 text-emerald-400" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-100">
                  {l.borrower_or_entity_name}{" "}
                  <span className="text-slate-400">· up to {fmtCurrency(l.loan_amount)}</span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                  <span>{fmtDateTime(l.created_at)}</span>
                  {l.created_by_name && <span>· by {l.created_by_name}</span>}
                  {(l.sent_to || []).length > 0 && (
                    <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[10px]">
                      Sent to {l.sent_to.length}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadLetter(l)}
                  className="border-slate-700 bg-slate-950 text-slate-200 hover:bg-slate-800"
                  data-testid={`pa-download-${l.letter_id}`}
                >
                  <Download className="mr-1.5 h-3.5 w-3.5" /> Download
                </Button>
                <Button
                  size="sm"
                  onClick={() => openSend(l)}
                  className="bg-emerald-600 text-white hover:bg-emerald-500"
                  data-testid={`pa-send-${l.letter_id}`}
                >
                  <Send className="mr-1.5 h-3.5 w-3.5" /> Send
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(l)}
                  className="text-rose-300 hover:bg-rose-500/10"
                  data-testid={`pa-delete-${l.letter_id}`}
                  aria-label="Delete letter"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Generate dialog */}
      <Dialog open={genOpen} onOpenChange={(o) => !o && !busy && setGenOpen(false)}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-50 sm:max-w-md" data-testid="pa-generate-dialog">
          <DialogHeader>
            <DialogTitle>Generate pre-approval letter</DialogTitle>
            <DialogDescription className="text-slate-400">
              The letter uses the official PMB Loans template — signed by Ariel Lopez, Managing Partner.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={generate} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-[0.15em]">LLC name or borrower name</Label>
              <Input
                value={form.borrower_or_entity_name}
                onChange={(e) => setForm((f) => ({ ...f, borrower_or_entity_name: e.target.value }))}
                placeholder="e.g. 123 Main Holdings LLC"
                className="mt-1.5 border-slate-800 bg-slate-950 text-slate-50"
                required
                data-testid="pa-name-input"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                This is what will print on the letter. Edit freely — pre-filled from the application.
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-[0.15em]">Pre-approved loan amount ($)</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={form.loan_amount}
                onChange={(e) => setForm((f) => ({ ...f, loan_amount: e.target.value }))}
                placeholder="450000"
                className="mt-1.5 border-slate-800 bg-slate-950 text-slate-50"
                required
                data-testid="pa-amount-input"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                Numeric only — formatted to currency on the letter.
              </div>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-[0.15em]">Letter date (optional)</Label>
              <Input
                value={form.letter_date}
                onChange={(e) => setForm((f) => ({ ...f, letter_date: e.target.value }))}
                placeholder="Defaults to today (e.g. June 24, 2026)"
                className="mt-1.5 border-slate-800 bg-slate-950 text-slate-50"
                data-testid="pa-date-input"
              />
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setGenOpen(false)}
                disabled={busy}
                className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
                data-testid="pa-generate-submit"
              >
                {busy ? "Generating…" : "Generate letter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send dialog */}
      <Dialog open={sendOpen} onOpenChange={(o) => !o && !sending && setSendOpen(false)}>
        <DialogContent className="border-slate-800 bg-slate-900 text-slate-50 sm:max-w-md" data-testid="pa-send-dialog">
          <DialogHeader>
            <DialogTitle>Send letter</DialogTitle>
            <DialogDescription className="text-slate-400">
              {sendTarget && (
                <>
                  {sendTarget.borrower_or_entity_name} · up to {fmtCurrency(sendTarget.loan_amount)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-800 bg-slate-950 p-3 text-sm">
              <Checkbox
                checked={recipients.broker}
                onCheckedChange={(v) => setRecipients((r) => ({ ...r, broker: !!v }))}
                disabled={!app.sponsor_id}
                data-testid="pa-send-broker"
              />
              <div>
                <div className="text-slate-100">Broker</div>
                <div className="text-xs text-slate-500">{app.sponsor_name || "—"}</div>
              </div>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-800 bg-slate-950 p-3 text-sm">
              <Checkbox
                checked={recipients.borrower}
                onCheckedChange={(v) => setRecipients((r) => ({ ...r, borrower: !!v }))}
                disabled={!app.borrower_email}
                data-testid="pa-send-borrower"
              />
              <div>
                <div className="text-slate-100">Borrower</div>
                <div className="text-xs text-slate-500">{app.borrower_email || "—"}</div>
              </div>
            </label>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-[0.15em]">Other recipients (comma-separated)</Label>
              <Input
                value={recipients.extra}
                onChange={(e) => setRecipients((r) => ({ ...r, extra: e.target.value }))}
                placeholder="title@company.com, agent@example.com"
                className="mt-1.5 border-slate-800 bg-slate-950 text-slate-50"
                data-testid="pa-send-extra"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSendOpen(false)}
              disabled={sending}
              className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
            >
              Cancel
            </Button>
            <Button
              onClick={submitSend}
              disabled={sending}
              className="bg-emerald-600 text-white hover:bg-emerald-500 btn-glow disabled:opacity-60"
              data-testid="pa-send-submit"
            >
              <Send className="mr-2 h-4 w-4" />
              {sending ? "Sending…" : "Send letter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
